import type { CSSProperties, ReactNode } from 'react';
import { useMemo } from 'react';

import { getRandomInt } from '~utils/number';

type BubblePositions = { outerTop: number; outerLeft: number; innerTop: number; innerLeft: number };

type TopicPositionerProps = {
  children: ReactNode;
  position: BubblePositions;
  active: boolean;
  activeSibling?: boolean;
};

function useBubblePosition(position: BubblePositions, active: boolean, activeSibling: boolean) {
  const randomness = 0;
  const style: CSSProperties = useMemo(() => {
    if (active) {
      // Center Position
      return { top: '40%', left: '50%' };
    }
    if (activeSibling) {
      // Outer position
      const top = Math.max(0, position.outerTop + getRandomInt(-randomness, randomness));
      const left = Math.max(0, position.outerLeft + getRandomInt(-randomness, randomness));
      return { top: `${top}%`, left: `${left}%` };
    }
    // Inner position
    const top = Math.max(0, position.innerTop + getRandomInt(-randomness, randomness));
    const left = Math.max(0, position.innerLeft + getRandomInt(-randomness, randomness));
    return { top: `${top}%`, left: `${left}%` };
  }, [position.outerTop, position.outerLeft, active, activeSibling, position.innerLeft, position.innerTop]);
  return style;
}

export function BubblePositioner({ children, position, active, activeSibling = false }: TopicPositionerProps) {
  const style = useBubblePosition(position, active, activeSibling);
  return (
    <div
      className={`absolute w-0 h-0 flex items-center justify-center transition-all duration-500 transform-gpu ${
        active || !activeSibling ? 'opacity-100 scale-100' : 'opacity-30 scale-75'
      }`}
      style={style}
    >
      {children}
    </div>
  );
}
